import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api/client";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      const hasWs = !!localStorage.getItem("avida_workspace");
      if (u?.role === "field_agent") nav("/field");
      else if (!hasWs) nav("/choose");
      else nav(u?.role === "staff" ? "/my-day" : "/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-stretch bg-[#FAFBFC]">
      {/* Left brand panel — navy hero with real logo */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 bg-[#15233B] text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_20%,#FFCB05_0,transparent_45%),radial-gradient(circle_at_75%_70%,#1E73C9_0,transparent_45%)]"
        />
        <div className="relative">
          <img
            src="/brand/avida-logo.png"
            alt="Avida Personal Care"
            className="h-28 w-auto"
            draggable={false}
          />
        </div>
        <div className="relative max-w-lg">
          <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            The right tools for <span className="text-[#FFCB05]">your goals.</span>
          </h1>
          <p className="mt-6 text-white/80 text-base leading-relaxed">
            The internal intake workspace for Avida Personal Care — 16 fixed stages,
            gated tasks, blocked-flag transparency, and a daily digest you can trust.
          </p>
          <div className="mt-6 rounded-lg border border-[#FFCB05]/40 bg-[#FFCB05]/10 px-4 py-3">
            <p className="text-[13px] font-semibold text-[#FFCB05] leading-relaxed">
              DO NOT add PHI or HIPAA prohibited data into this tool.
            </p>
          </div>
          <div className="mt-8 flex items-center gap-3">
            <span className="h-1.5 w-10 rounded-full bg-[#FFCB05]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/55">Internal Avida team only</span>
          </div>
        </div>
        <div className="relative text-xs uppercase tracking-[0.3em] text-white/45">
          Avida Healthcare Software Solutions · Personal Care Agency
        </div>
      </div>

      {/* Right login card */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-[#FFCB05] relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.18] bg-[radial-gradient(circle_at_85%_20%,#FFFFFF_0,transparent_55%),radial-gradient(circle_at_10%_90%,#1E73C9_0,transparent_55%)]"
        />
        <img
          src="/brand/avida-logo.png"
          alt=""
          aria-hidden
          draggable={false}
          className="pointer-events-none select-none absolute -right-24 -bottom-32 w-[820px] max-w-[120%] opacity-[0.10] mix-blend-multiply"
        />
        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="relative w-full max-w-md bg-white border border-[#15233B]/10 rounded-xl shadow-2xl p-8"
        >
          <div className="flex items-center gap-3 lg:hidden mb-4">
            <img src="/brand/avida-logo.png" alt="Avida" className="h-10 w-auto" />
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#1E73C9] font-bold">Sign in</div>
          <h2 className="text-2xl font-extrabold tracking-tight mt-1 text-[#15233B]">Welcome back</h2>
          <p className="text-sm text-[#15233B]/60 mt-1">Use your Avida team email.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-bold text-[#15233B]/70 mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-[#15233B]/15 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/40 focus:border-[#1E73C9] bg-white transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-bold text-[#15233B]/70 mb-2">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-[#15233B]/15 rounded-md px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/40 focus:border-[#1E73C9] bg-white transition-colors"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-[#15233B]/50 hover:text-[#1E73C9] hover:bg-[#1E73C9]/5"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#15233B] hover:bg-[#1E73C9] disabled:opacity-60 text-white font-semibold py-2.5 rounded-md transition-colors relative overflow-hidden group"
            >
              <span className="relative z-10">{loading ? "Signing in…" : "Sign in"}</span>
              <span className="absolute inset-0 bg-[#FFCB05] opacity-0 group-hover:opacity-10 transition-opacity" />
            </button>
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-[#15233B]/10" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/40">or</span>
              <span className="h-px flex-1 bg-[#15233B]/10" />
            </div>
            <button
              type="button"
              data-testid="google-signin-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full border border-[#15233B]/15 hover:bg-[#15233B]/5 text-[#15233B] font-semibold py-2.5 rounded-md transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.49 12c0-.73.13-1.44.35-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.94l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.16-3.16A11 11 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
              </svg>
              Continue with Google
            </button>
            <p className="text-[11px] text-[#15233B]/45 text-center">Google sign-in works only for existing team accounts.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
